import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import ReportService from "@services/ReportService";
import { ORGANIZATION_TYPES } from "@constants";
import { Loader, Modal, ConfirmationModal } from "../../../../../components";
import "./ReportAdminModal.scss";
import ClientIcon from "../../../../../images/reportIcons/client-report.svg?svgr";
import InvoiceIcon from "../../../../../images/reportIcons/invoice-report.svg?svgr";
import LocationIcon from "../../../../../images/reportIcons/location-report.svg?svgr";
import OfficerIcon from "../../../../../images/reportIcons/officer-report.svg?svgr";
import ReceiptIcon from "../../../../../images/reportIcons/payment-report.svg?svgr";
import TransactionIcon from "../../../../../images/reportIcons/transaction-report.svg?svgr";
import AgencyIcon from "../../../../../images/reportIcons/agency-report.svg?svgr";
import DefaultIcon from "../../../../../images/reportIcons/custom-report.svg?svgr";
import RollKallIcon from "../../../../../images/reportIcons/rollkall-report.svg?svgr";

const ReportAdminModal = props => {
	const { lang } = global;
	const { show, onHide, organization, user, refreshOrganization } = props;

	const _confirmationModal = useRef(null);
	const [orgReports, setReports] = useState([]);
	const [loadingOrganizationReports, setLoadingOrganizationReports] = useState([]);
	const [loading, setLoading] = useState(false);
	const [form, setForm] = useState([]);
	const [showNewReport, SetNewReportForm] = useState(false);
	const [editWindowOpen, SetEditWindowOpen] = useState(false);
	const [selectedIcon, setSelectedIcon] = useState("default");
	const [reportingTier, setReportingTier] = useState(0);
	const nonCoordinatorAccounts = ["Client", "Coordinator", "Security Agency"];

	const reportIcon = {
		client: ClientIcon,
		invoice: InvoiceIcon,
		location: LocationIcon,
		officer: OfficerIcon,
		receipt: ReceiptIcon,
		transaction: TransactionIcon,
		agency: AgencyIcon,
		default: DefaultIcon,
        rollkall: RollKallIcon
	};

	const reportIconList = [
		{ value: "default", label: "Default" },
		{ value: "client", label: "Clients" },
		{ value: "agency", label: "Departments" },
		{ value: "invoice", label: "Invoices" },
		{ value: "location", label: "Locations" },
		{ value: "officer", label: "Officers" },
		{ value: "receipt", label: "Receipts" },
		{ value: "transaction", label: "Transactions" },
        { value: "rollkall", label: "RollKall" },
	];

	const reportingTierList = [
		{ value: 0, label: "None" },
		{ value: 1, label: "STANDARD" },
		{ value: 2, label: "PRO" },
	];

	const mapReportObject = () => ({
		Name: form.Name,
		EmbedCode: form.EmbedCode,
		IsadminEnabled: form.IsAdminEnabled,
		IscoordinatorEnabled: form.IsCoordinatorEnabled,
		IsstaffEnabled: form.IsStaffEnabled,
		IsViewOnlyEnabled: form.IsViewOnlyEnabled,
		Category: form.Category.toUpperCase(),
		Description: form.Description,
		OrganizationId: organization.Id,
		UserTypeId: ORGANIZATION_TYPES.find(
			({ label }) => label.toString() === organization.TypeDescription,
		)?.value,
	});

	const getReportsByUserType = async () =>
		ReportService.get({
			endpoint: "reports/getreportsbyusertype",
			queryParams: {
				organizationId: organization.Id,
				userTypeId: user.Organization.organizationTypeId,
				allOrgReports: true,
			},
		});

	const loadOrganizationReports = async () => {
		try {
			setLoadingOrganizationReports(true);
			const response = await getReportsByUserType();
			const reports = response.data.sort((a, b) =>
				a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1,
			);
			setReports(reports);
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to retrieve organization reports.",
				timeOut: 1500,
			});
		} finally {
			setLoadingOrganizationReports(false);
		}
	};

	const onInputChange = (field, value) => {
		if (field === "Category") {
			setSelectedIcon(value);
		}
		setForm({
			...form,
			[field]: value,
		});
	};

	const resetInputs = () => {
		// clearing input form fields not tied to state
		document.getElementById("newReportName").value = "";
		document.getElementById("newReportCode").value = "";
		document.getElementById("newReportDescription").value = "";
		document.getElementById("reportNewEnableAdmin").checked = false;
		document.getElementById("reportNewEnableCoord").checked = false;
		document.getElementById("reportNewEnableStaff").checked = false;
		document.getElementById("reportNewEnableViewOnly").checked = false;
	};

	const onNewReportChange = () => {
		loadOrganizationReports(user, organization.Id);
		setLoading(false);
		// defines form.Category when "add report" button is clicked to prevent 'undefined' typevalue errors
		const setFormCategory = "Category";
		const setFormCategoryValue = "default";
		setForm({
			[setFormCategory]: setFormCategoryValue,
		});
		// clear out potential carry overs
		resetInputs();
		SetEditWindowOpen(false);
		setSelectedIcon("default");
		SetNewReportForm(!showNewReport);
	};

	const handleShowReport = reportId => {
		const updateReports = orgReports.map(report => {
			if (report.Id === reportId) {
				setForm(report);
				setSelectedIcon(report.Category?.toLowerCase() || "default");
				return {
					...report,
					show: !report.show,
				};
			}
			return report;
		});
		setReports(updateReports);
		SetEditWindowOpen(!editWindowOpen);
	};

	useEffect(() => {
		if (show) {
			loadOrganizationReports(user, organization.Id);
			setReportingTier(organization.EnhancedReportingTier);
		}
	}, [show]);

	const validateFields = () => {
		const formFields = { ...form };
		let formIsValid = true;

		// Name
		if (!formFields.Name) {
			formIsValid = false;
			utils.notify({
				type: "error",
				message: "Report Name Cannot Be Blank.",
				timeOut: 1500,
			});
		}

		// EmbedCode
		if (!formFields.EmbedCode) {
			formIsValid = false;
			utils.notify({
				type: "error",
				message: "Embed Code Cannot Be Blank.",
				timeOut: 1500,
			});
		}

		// Embed Code Length
		if (formFields.EmbedCode?.length !== 5) {
			formIsValid = false;
			utils.notify({
				type: "error",
				message: "Invalid Embed Code Length.",
				timeOut: 1500,
			});
		}

		return formIsValid;
	};

	const saveReport = async () => {
		if (validateFields()) {
			try {
				setLoading(true);
				const report = mapReportObject();
				await ReportService.createReport(report);
			} catch (error) {
				utils.notify({
					type: "error",
					message: "Unable to Create Report.",
					timeOut: 1500,
				});
			} finally {
				onNewReportChange();
			}
		}
	};

	const updateReport = async reportId => {
		try {
			setLoading(true);
			await ReportService.updateReport(form);
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to Update Report.",
				timeOut: 1500,
			});
		} finally {
			loadOrganizationReports(user, organization.Id);
			handleShowReport(reportId);
			setLoading(false);
		}
	};

	const deleteReport = async reportId => {
		try {
			setLoading(true);
			await ReportService.deleteReport(reportId);
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to Delete Report.",
				timeOut: 1500,
			});
		} finally {
			loadOrganizationReports(user, organization.Id);
			SetEditWindowOpen(false);
			setForm();
			setLoading(false);
		}
	};

	const changeReportingTier = async tier => {
		try {
			setLoading(true);
			await ReportService.changeReportTier(
				organization.Id,
				user.Organization.OrganizationTypeId,
				reportingTierList[tier].label,
				tier,
			);

            if (refreshOrganization) {
                await refreshOrganization();
            }

            setReportingTier(tier)
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to Change Reporting Tier.",
				timeOut: 1500,
			});
		} finally {
			loadOrganizationReports(user, organization.Id);
			SetEditWindowOpen(false);
			setForm();
			setLoading(false);
		}
	};

	const confirmChangeTierModal = tier => {
		if (reportingTier !== tier && tier > 0) {
			_confirmationModal.current.openModal({
				title: lang.t("Change Reporting Tier"),
				message: [
					<div className="text-center">
						{lang.t("Changing reporting tier may affect billing_")}
					</div>,
				],
				confirmButton: `${lang.t("Yes, Proceed")}`,
				onConfirm: () => changeReportingTier(tier),
				cancelButton: `${lang.t("No, Cancel")}`,
			});
		}
	};

	const confirmDeleteModal = reportId => {
		_confirmationModal.current.openModal({
			title: lang.t("Delete Report"),
			message: [
				<div className="text-center">
					{lang.t(
						"Deleting report will remove visibility for all users in the Organization_",
					)}
				</div>,
			],
			confirmButton: `${lang.t("Yes, Proceed")}`,
			onConfirm: () => deleteReport(reportId),
			cancelButton: `${lang.t("No, Cancel")}`,
		});
	};

	const clearForm = () => {
		SetEditWindowOpen(false);
		setForm();
		onHide();
	};

	return (
		<>
			<Modal
				id="report-admin-modal"
				show={show}
				size="lg"
				onHide={() => clearForm()}
			>
				<Modal.Header
					closeButton
					className="hidden-print"
				>
					<Modal.Title>{lang.t("Configure Reports")}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="container-fluid">
					<div style={{ marginLeft: "15px", marginRight: "15px" }}>
						<p className="align-left" />
						<p className="align-center reports-table">User Roles</p>
						<span className="align-right">
							<label htmlFor="editReportTier">
								<span className="report-tier-label">Reporting Tier</span>
								<select
									className="reports-table"
									value={reportingTier}
									onChange={e => confirmChangeTierModal(e.target.value)}
								>
									{reportingTierList.map(tier => (
										<option
											key={tier.value}
											value={tier.value}
										>
											{tier.label}
										</option>
									))}
								</select>
							</label>
						</span>

						<table id="reportsTable">
							<tr className="table-header">
								<th className="col-md-2 col-xs-6 reports-column-header">
									<h4>Report</h4>
								</th>
								<th className="col-md-2 col-xs-6 reports-table">
									<h4>Admin</h4>
								</th>
								<th className="col-md-2 col-xs-6 reports-table">
									<h4>Coordinator</h4>
								</th>
								<th className="col-md-2 col-xs-6 reports-table">
									<h4>Staff</h4>
								</th>
								<th className="col-md-2 col-xs-6 reports-table">
									<h4>View Only</h4>
								</th>
								<th
									className="col-md-2 col-xs-6 reports-table"
									aria-label="Configure Report Dropdowns"
								/>
							</tr>
							<For
								each="report"
								of={orgReports}
							>
								<tr className={`${!report.show ? "report-row" : "report-edit"}`}>
									<If condition={report.show}>
										<td className="new-report-header">
											<label htmlFor="editReportName">
												Report Name
												<input
													className="form-control embed-text-box"
													name="editReportName"
													id="editReportName"
													type="text"
													defaultValue={report.Name}
													onChange={e =>
														onInputChange("Name", e.target.value)
													}
												/>
											</label>
										</td>
									</If>
									<If condition={!report.show}>
										<td>
											<h5>
												<strong>{report.Name}</strong>
											</h5>
										</td>
									</If>
									<td className="col-md-2 col-xs-6 report-table">
										<If condition={report.show}>
											<input
												type="checkbox"
												id="reportNewEnableAdmin"
												name="reportNewEnableAdmin"
                                                disabled={report.CustomFilter}
												defaultChecked={report.IsAdminEnabled}
												onChange={e =>
													onInputChange(
														"IsAdminEnabled",
														e.target.checked,
													)
												}
											/>
										</If>
										<If condition={!report.show}>
											<If condition={report.IsAdminEnabled}>
												<button
													type="button"
													className="btn btn-link"
												>
													<i className="rkf rkf-check-mark" />
												</button>
											</If>
										</If>
									</td>
									<td className="col-md-2 col-xs-6 report-table">
										<If condition={report.show}>
											<input
												type="checkbox"
												id="reportEditEnableCoord"
												name="reportEditEnableCoord"
												disabled={nonCoordinatorAccounts.includes(
													organization.TypeDescription,
												) || report.CustomFilter}
												defaultChecked={report.IsCoordinatorEnabled}
												onChange={e =>
													onInputChange(
														"IsCoordinatorEnabled",
														e.target.checked,
													)
												}
											/>
										</If>
										<If condition={!report.show}>
											<If condition={report.IsCoordinatorEnabled}>
												<button
													type="button"
													className="btn btn-link"
												>
													<i className="rkf rkf-check-mark" />
												</button>
											</If>
										</If>
									</td>
									<td className="col-md-2 col-xs-6 report-table">
										<If condition={report.show}>
											<input
												type="checkbox"
												id="reportEditEnableStaff"
												name="reportEditEnableStaff"
                                                disabled={report.CustomFilter}
												defaultChecked={report.IsStaffEnabled}
												onChange={e =>
													onInputChange(
														"IsStaffEnabled",
														e.target.checked,
													)
												}
											/>
										</If>
										<If condition={!report.show}>
											<If condition={report.IsStaffEnabled}>
												<button
													type="button"
													className="btn btn-link"
												>
													<i className="rkf rkf-check-mark" />
												</button>
											</If>
										</If>
									</td>
									<td className="col-md-2 col-xs-6 report-table">
										<If condition={report.show}>
											<input
												type="checkbox"
												id="reportEditEnableViewOnly"
												name="reportEditEnableViewOnly"
                                                disabled={report.CustomFilter}
												defaultChecked={report.IsViewOnlyEnabled}
												onChange={e =>
													onInputChange(
														"IsViewOnlyEnabled",
														e.target.checked,
													)
												}
											/>
										</If>
										<If condition={!report.show}>
											<If condition={report.IsViewOnlyEnabled}>
												<button
													type="button"
													className="btn btn-link"
												>
													<i className="rkf rkf-check-mark" />
												</button>
											</If>
										</If>
									</td>
									<If
										condition={
											(!report.show && !editWindowOpen && !showNewReport) ||
											report.show
										}
									>
										<td className="col-md-2 col-xs-6 reports-table">
											<button
												type="button"
												className="btn btn-link padding-left-10"
												onClick={() => {
													handleShowReport(report.Id);
												}}
											>
												<i
													style={{ cursor: "pointer" }}
													className="h4 rkf rkf-edit"
												/>
											</button>
										</td>
									</If>
									<If
										condition={
											(!report.show && editWindowOpen) || showNewReport
										}
									>
										<td className="col-md-2 col-xs-6 reports-table">
											<button
												type="button"
												className="btn btn-link btn-disabled padding-left-10"
												disabled
												onClick={() => {
													handleShowReport(report.Id);
												}}
											>
												<i
													style={{ cursor: "pointer" }}
													className="h4 rkf rkf-edit"
												/>
											</button>
										</td>
									</If>
								</tr>
								<tr
									className={`${report.show ? "report-row-show edit-description" : "report-row-hidden"}`}
								>
									<td className="new-report-header edit-report-icon">
										{React.createElement(reportIcon[selectedIcon], {
											className: "edit-report-icon-preview",
										})}
										<label htmlFor="editReportIcon">
											<span className="report-icon-label">Report Icon</span>
											<select
												className="form-control"
												defaultValue={
													report.Category?.toLowerCase() ||
													reportIcon.default
												}
												onChange={e =>
													onInputChange("Category", e.target.value)
												}
											>
												{reportIconList.map(icon => (
													<option
														key={icon.value}
														value={icon.value}
													>
														{icon.label}
													</option>
												))}
											</select>
										</label>
									</td>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td className="new-report-header edit-spacing description-spacing">
										<label htmlFor="editReportDescription">
											Description
											<textarea
												className="form-control"
												name="editReportDescription"
												id="editReportDescription"
												type="textarea"
												defaultValue={report.Description}
												maxLength="250"
												rows="4"
												onChange={e =>
													onInputChange("Description", e.target.value)
												}
											/>
										</label>
									</td>
								</tr>
								<tr
									className={`${report.show ? "report-row-show edit-description" : "report-row-hidden"}`}
								>
									<td className="new-report-header">
										<label htmlFor="editReportCode">
											Embed Code
											<input
												className="form-control embed-text-box"
												name="editReportCode"
												id="editReportCode"
												type="text"
												Value={report.EmbedCode}
												onChange={e =>
													onInputChange("EmbedCode", e.target.value)
												}
											/>
										</label>
									</td>
								</tr>
								<tr
									className={`${report.show ? "report-row-show edit" : "report-row-hidden"}`}
								>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td class="col-md-2 col-xs-6 reports-table">
										<button
											type="button"
											className="btn btn-link"
											aria-label="Confirm Delete"
											Value={report.Id}
											onClick={() => confirmDeleteModal(report.Id)}
										>
											<i className="rkf rkf-trash font-size-18 text-danger" />
										</button>
									</td>
								</tr>
							</For>
							<tr
								className={`${showNewReport ? "report-row-show" : "report-row-hidden"}`}
							>
								<td className="new-report-header">
									<label htmlFor="newReportName">
										Report Name
										<input
											className="form-control embed-text-box"
											name="newReportName"
											id="newReportName"
											type="text"
											onChange={e => onInputChange("Name", e.target.value)}
										/>
									</label>
								</td>
								<td className="col-md-2 col-xs-6 new-report-table">
									<input
										type="checkbox"
										id="reportNewEnableAdmin"
										name="reportNewEnableAdmin"
										onChange={e =>
											onInputChange("IsAdminEnabled", e.target.checked)
										}
									/>
								</td>
								<td className="col-md-2 col-xs-6 new-report-table">
									<input
										type="checkbox"
										id="reportNewEnableCoord"
										name="reportNewEnableCoord"
										disabled={nonCoordinatorAccounts.includes(
											organization.TypeDescription,
										)}
										onChange={e =>
											onInputChange("IsCoordinatorEnabled", e.target.checked)
										}
									/>
								</td>
								<td className="col-md-2 col-xs-6 new-report-table">
									<input
										type="checkbox"
										id="reportNewEnableStaff"
										name="reportNewEnableStaff"
										onChange={e =>
											onInputChange("IsStaffEnabled", e.target.checked)
										}
									/>
								</td>
								<td className="col-md-2 col-xs-6 new-report-table">
									<input
										type="checkbox"
										id="reportNewEnableViewOnly"
										name="reportNewEnableViewOnly"
										onChange={e =>
											onInputChange("IsViewOnlyEnabled", e.target.checked)
										}
									/>
								</td>
							</tr>
							<tr
								className={`${showNewReport ? "report-row-show" : "report-row-hidden"}`}
							>
								<td className="new-report-header new-report-icon">
									{React.createElement(reportIcon[selectedIcon], {
										className: "new-report-icon-preview",
									})}
									<label htmlFor="reportIcon">
										<span className="report-icon-label">Report Icon</span>
										<select
											className="form-control"
											value={selectedIcon}
											onChange={e =>
												onInputChange("Category", e.target.value)
											}
										>
											{reportIconList.map(icon => (
												<option
													key={icon.value}
													value={icon.value}
												>
													{icon.label}
												</option>
											))}
										</select>
									</label>
								</td>
								<td
									className="col-md-2 col-xs-6"
									aria-label="Forced Spacing"
								/>
								<td className="new-report-header edit-spacing description-spacing">
									<label htmlFor="newReportDescription">
										Description
										<textarea
											className="form-control"
											name="newReportDescription"
											id="newReportDescription"
											type="textarea"
											maxLength="250"
											rows="4"
											onChange={e =>
												onInputChange("Description", e.target.value)
											}
										/>
									</label>
								</td>
							</tr>
							<tr
								className={`${showNewReport ? "report-row-show" : "report-row-hidden"}`}
							>
								<td className="new-report-header">
									<label htmlFor="newReportCode">
										Embed Code
										<input
											className="form-control embed-text-box"
											name="newReportCode"
											id="newReportCode"
											type="text"
											onChange={e =>
												onInputChange("EmbedCode", e.target.value)
											}
										/>
									</label>
								</td>
							</tr>
							<tr>
								<td>
									<button
										type="button"
										className="btn btn-link col-md-2 col-xs-6 reports-column-header"
										onClick={() => {
											onNewReportChange();
										}}
									>
										{!showNewReport ? "+ Add Report" : "- Add Report"}
									</button>
								</td>
								<If condition={showNewReport}>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td
										className="col-md-2 col-xs-6"
										aria-label="Forced Spacing"
									/>
									<td className="col-md-2 col-xs-6 new-report-table">
										<button
											type="button"
											className="btn btn-primary"
											onClick={saveReport}
										>
											{loading ? "Adding..." : "Add"}
										</button>
									</td>
								</If>
							</tr>
						</table>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<If condition={editWindowOpen}>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => {
								updateReport(form.id);
							}}
						>
							{loading ? "Updating..." : "Update"}
						</button>
					</If>
					<If condition={!editWindowOpen}>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => clearForm()}
						>
							{lang.t("Close")}
						</button>
					</If>
				</Modal.Footer>
				<Loader active={loadingOrganizationReports} />
			</Modal>
			<ConfirmationModal ref={_confirmationModal} />
		</>
	);
};

ReportAdminModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	organization: PropTypes.object,
	user: PropTypes.object,
    refreshOrganization: PropTypes.func
};

ReportAdminModal.defaultProps = {
	show: false,
	onHide: () => {},
	organization: {},
	user: {},
    refreshOrganization: () => {},
};

export default ReportAdminModal;
